import ColumnFilterGroups from "components/ColumnFilterGroups";
import ColumnFilterKeyValue from "components/ColumnFilterKeyValue";
import { getVocabulary } from "components/Vocabulary";
import { useHasDisabledEnvironment } from "contexts/GlobalSettingsContext";
import { useGlobalFilterContext } from "contexts/GlobalFilterContext";
import _ from "lodash";
import React, { useMemo, useState } from "react";
import useTrigerrableEffect from "react-triggerable-effect";
import ScanAPI from "restAPI/ScanAPI";
import {
  CellRenderers,
  ColumnFilterTypes,
  getFilterProps,
  startCaseWithSpace,
} from "utils/tableUtils";
import { toolsName } from "views/GlobalSettings/ToolsName";
import {
  SCAN_FINDING_LIST,
  TRIGGER_BY_NAME_MAPPING,
} from "views/scanresult/ScanListProvider";

const useScanFilters = (addFindingChangeFilter = false) => {
  const [filterValues, setFilterValues] = useState({});
  const { hasDisabledEnvironment } = useHasDisabledEnvironment();
  const { globalFilterHash } = useGlobalFilterContext();
  const refreshFilters = useTrigerrableEffect(() => {
    const { cancel, promise } = ScanAPI.getFilters();
    promise.then((data) => {
      setFilterValues({
        ...data,
        scanStatus: data.status,
        findings: SCAN_FINDING_LIST,
      });
    });
    return cancel;
  }, [globalFilterHash]);

  const scanFilterColumns = useMemo(() => {
    return getScanFilterColumns({
      filterValues,
      addFindingChangeFilter,
      hasDisabledEnvironment,
    });
  }, [filterValues, addFindingChangeFilter, hasDisabledEnvironment]);
  return {
    scanFilterColumns,
    filterValues,
    refreshFilters,
  };
};

export function getScanFilterColumns({
  filterValues = {},
  addFindingChangeFilter = false,
  hasDisabledEnvironment = false,
} = {}) {
  const columns = [
    {
      title: "ID",
      dataIndex: "id",
      ...getFilterProps({ onlySearch: true, isMultipleValueSupported: true }),
    },
    {
      title: getVocabulary("product"),
      dataIndex: "product",
      ...getFilterProps({ filters: filterValues.product }),
      filterIndex: 1,
    },
    {
      title: getVocabulary("subProduct"),
      dataIndex: "subProduct",
      ...getFilterProps({ filters: filterValues.subproduct }),
      filterIndex: 3,
    },
    ...(hasDisabledEnvironment
      ? []
      : [
          {
            title: "Environment",
            dataIndex: "environmentName",
            ...getFilterProps({
              filters: filterValues.environment,
              filterLabelFormatter: CellRenderers.VOCABULARY_RENDERER,
            }),
            filterIndex: 2,
          },
        ]),
    {
      title: "Source Tool",
      dataIndex: "source",
      ...getFilterProps({
        filters: filterValues.tool || _.uniq(Object.values(toolsName)),
        filterLabelFormatter: (tool) => tool,
      }),
    },
    {
      title: "Type",
      dataIndex: "scanType",
      ...getFilterProps({
        filters: filterValues.scanType,
        nullFilterValue: null,
      }),
    },
    {
      title: "Ingested",
      dataIndex: "createdAt",
      ...getFilterProps({
        filterType: ColumnFilterTypes.DATE_RANGE,
        filterLabelFormatter: CellRenderers.DATE,
      }),
    },
    {
      title: "Last Tool Scan",
      dataIndex: "scanDate",
      ...getFilterProps({
        filterType: ColumnFilterTypes.DATE_RANGE,
        filterLabelFormatter: CellRenderers.DATE,
        allowFutureDates: true,
      }),
    },
    {
      title: "Status",
      dataIndex: "scanStatus",
      ...getFilterProps({
        filters: filterValues.status,
        filterLabelFormatter: _.capitalize,
      }),
    },
    {
      title: "Trigger",
      dataIndex: "triggerby",
      ...getFilterProps({
        filters: filterValues.triggerby,
        filterLabelFormatter: (text) =>
          TRIGGER_BY_NAME_MAPPING[text] || startCaseWithSpace(text),
      }),
    },
    {
      title: "Tags",
      dataIndex: "tags",
      filterDropdown: (props) => <ColumnFilterKeyValue {...props} />,
      filterLabelFormatter: ColumnFilterKeyValue.filterLabelFormatter,
    },
    {
      title: "Scan Identifier",
      dataIndex: "scanIdentifier",
      ...getFilterProps({
        onlySearch: true,
        nullFilterValue: null,
        filterLabelFormatter: CellRenderers.VALUE_OR_NA,
      }),
    },
    {
      title: "Uploaded By",
      dataIndex: "uploadedBy",
      ...getFilterProps({
        onlySearch: true,
        nullFilterValue: null,
        filterLabelFormatter: CellRenderers.VALUE_OR_NA,
      }),
    },
    {
      title: `${getVocabulary("group")}`,
      dataIndex: "groups",
      filterDropdown: (props) => <ColumnFilterGroups {...props} newVersion={true} />,
      filterLabelFormatter: ColumnFilterGroups.filterLabelFormatter,
    }
  ];

  if (addFindingChangeFilter) {
    columns.push({
      title: "Change",
      dataIndex: "findings",
      key: "findings",
      ...getFilterProps({
        filters: filterValues.findings,
        filterLabelFormatter: (findings) => `${_.capitalize(findings)} > 0`,
      }),
    });
  }

  return columns.map((c) => ({
    ...c,
    key: c.key || c.dataIndex,
    filterLabelFormatter: c.filterLabelFormatter || CellRenderers.VALUE_OR_NA,
  }));
}

export default useScanFilters;
